import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { toInteger } from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useAuth } from "~/Hooks/useAuth";
import { eramanCMSApi, post, put } from "~/Services";
import { eramanHost } from "~/Utils";
import { useFaqs } from "../../Pages.hook";
import { fields } from "../../constants";

export const AddEditEramanFaqs = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();
  const [records, setRecords] = useState<any[]>([]);

  const {
    data: editNews,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetcheditNews,
  } = useFaqs(query.get("editId") || "");

  const {
    data: newsById,
    isLoading: isLoadingNews,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetcheditNews();
    }
  }, [query.get("editId"), query.get("id")]);

  const getRecords = () => {
    let url = `/api/faqs/all-fields`;
    eramanCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setRecords(envelopes);
        }
      })
      .catch(() => {
        setRecords([]);
      });
  };

  useEffect(() => {
    getRecords();
  }, []);

  const newsFormConfig: FormConfig = {
    initialValues: {
      field_sequence: decodeHTML(newsById?.payload?.field_sequence || editNews?.[0]?.field_sequence || ""),

      title: decodeHTML(newsById?.payload?.title || editNews?.[0]?.title || ""),
      field_common_malay_title: decodeHTML(
        newsById?.payload?.field_common_malay_title || editNews?.[0]?.field_common_malay_title || ""
      ),
      field_common_mandarin_title: decodeHTML(
        newsById?.payload?.field_common_mandarin_title || editNews?.[0]?.field_common_mandarin_title || ""
      ),

      field_content_description_zh: decodeHTML(
        newsById?.payload?.field_content_description_zh || editNews?.[0]?.field_content_description_zh || ""
      ),
      field_content_description_ms: decodeHTML(
        newsById?.payload?.field_content_description_ms || editNews?.[0]?.field_content_description_ms || ""
      ),
      field_content_description_en: decodeHTML(
        newsById?.payload?.field_content_description_en || editNews?.[0]?.field_content_description_en || ""
      ),

      published: decodeHTML(newsById?.payload?.published || editNews?.[0]?.published || ""),
    },
    // fields: fields.Faqs,
    fields: fields.Faqs.map((field: any) => {
      if (field.name === "field_sequence") {
        return {
          ...field,
          validation: {
            required: `${field.label || field.name} is required`,
            validate: (value: string, formValues: FieldValues) => {
              if (!value) return true;
              const existingRecord = records.find((record: any) => {
                return (
                  toInteger(record[field.name]) === toInteger(value) &&
                  record.id !== (editNews?.[0]?.id || newsById?.payload?.id)
                );
              });
              return !existingRecord || `${field.label || field.name} already exists`;
            },
          },
        };
      }

      return field;
    }),
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["newsImage"];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "ERAMAN");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }

    if (typeof data.published === "string") {
      data.published = data.published.toLowerCase() === "true";
    }

    const payload = {
      action: action,
      method: method,
      module: "Pages",
      subModule: "eramanPrivilegeCard_Faqs",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...data,
      },
      status: status,
    };
    console.log(payload, "payload");
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} FAQ`}
      showBack
    >
      <DynamicForm
        isLoading={isLoadingNews || drilldownLoading}
        config={newsFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${query.get("editId") ? `${eramanHost}/api/faq-s-list-data-crud-api/${editNews?.[0]?.id || newsById?.payload?.id}` : `${eramanHost}/api/create/faq-s-list-data-crud-api`}`,
            `${query.get("editId") ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") ? "PATCH" : "POST"}`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
      />
    </ContentWrapper>
  );
};
